import { useMemo } from "react";
import type { CreateInvestmentValues } from "@/components/shared/overlays/CreateInvestmentFields";
import type { QuotaCapacity } from "@/components/shared/entity/QuotaCapacityMeter";
import type { InvestmentState } from "@/domain/investment/types";
import { toNumber } from "@/utils/formatUtils";
import type { useInvestmentForm } from "./useInvestmentForm";

type Form = ReturnType<typeof useInvestmentForm>;

const toFormText = (value: number | null) => (value === null ? "" : String(value));

export function useInvestmentFormValues(investmentForm: Form, quotaName: string) {
  const data = investmentForm.form.watch();
  const { capacity } = investmentForm;

  const values: CreateInvestmentValues = {
    beneficiaryUuid: data.projectId ?? "",
    quotaUuid: data.quotaId || null,
    investorUuid: data.investorId ?? "",
    amount: toNumber(data.amount),
    returnPercentage: toNumber(data.returnPercentage),
    state: data.status ?? null,
  };

  const quota = useMemo<QuotaCapacity | null>(
    () =>
      capacity
        ? {
            name: quotaName,
            total: toNumber(capacity.total),
            completed: Number(capacity.completed),
            reserved: Number(capacity.reserved),
            available: capacity.room,
            left: toNumber(capacity.left),
          }
        : null,
    [capacity, quotaName],
  );

  const onChange = (changes: Partial<CreateInvestmentValues>) => {
    const { setValue } = investmentForm.form;
    const commit = (field: Parameters<typeof setValue>[0], value: unknown) =>
      setValue(field, value as never, { shouldValidate: true, shouldDirty: true });
    if (changes.beneficiaryUuid !== undefined) {
      commit("projectId", changes.beneficiaryUuid);
      commit("quotaId", "");
    }
    if (changes.quotaUuid !== undefined) commit("quotaId", changes.quotaUuid ?? "");
    if (changes.investorUuid !== undefined) {
      commit("investorId", changes.investorUuid);
      investmentForm.handleInvestorChange(changes.investorUuid);
    }
    if (changes.amount !== undefined) commit("amount", toFormText(changes.amount));
    if (changes.returnPercentage !== undefined)
      commit("returnPercentage", toFormText(changes.returnPercentage));
    if (changes.state !== undefined) commit("status", changes.state as InvestmentState);
  };

  return {
    values,
    quota,
    onChange,
    transferredDeduction: Number(capacity?.transferredDeduction ?? 0),
  };
}
